import React, { useEffect } from "react";
import styled from "@emotion/styled/macro";

import ParkkiTheme from "themes/ParkkiTheme";
import { RouteWithChildren } from "types/HeaderTypes";
import { Button, Menu, MenuItem } from "@mui/material";
import {
  HeaderTabsContextProps,
  useHeaderTabsContext,
} from "services/common/HeaderTabsProvider";

const Container = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  height: 100%;
`;

const StyledTab = styled.div`
  color: white;
  cursor: pointer;
  font-size: 16px;
  margin-right: 40px;
  padding-right: 8px;
  padding-left: 8px;
  text-align: center;
  white-space: nowrap;
`;

const SelectedTab = styled(StyledTab)`
  color: ${ParkkiTheme.colors.turquoise};
  padding-top: 10px;
  border-bottom: 5px solid ${ParkkiTheme.colors.turquoise};
  height: 26px;
`;

const StyledButton = styled(Button)`
  color: white;
  cursor: pointer;
  font-size: 16px;
  margin-right: 40px;
  padding-right: 8px;
  padding-left: 8px;
  text-align: center;
  white-space: nowrap;
  font-weight: normal;
  line-height: normal;
  letter-spacing: normal;
  border-radius: 0;
  font-family: "Source Sans Pro", sans-serif;
`;

const SelectedButton = styled(StyledButton)`
  color: ${ParkkiTheme.colors.turquoise};
  padding-top: 10px;
  border-bottom: 5px solid ${ParkkiTheme.colors.turquoise};
  height: 42px;
  &:hover {
    background-color: inherit !important;
  }
`;

type Props = {
  currentUri: string;
  onTabClick: Function;
  tabs: RouteWithChildren[];
};

function HeaderTabs({ currentUri, onTabClick, tabs }: Props): JSX.Element {
  const [anchorEl, setAnchorEl] = React.useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);

  const headerTabsContext: HeaderTabsContextProps | null =
    useHeaderTabsContext();

  const open = Boolean(anchorEl);
  useEffect(() => {
    headerTabsContext?.setHeaderTab(currentUri);
  }, [currentUri, headerTabsContext]);
  return (
    <Container>
      {tabs
        .filter((tab) => tab.hasAccess)
        .map((tab, index) => {
          const TabComponent =
            currentUri === tab?.route ||
            tab?.items?.find((i) => i.route === currentUri) !== undefined
              ? SelectedTab
              : StyledTab;

          if (tab.route) {
            return (
              <TabComponent
                onClick={() => onTabClick(tab)}
                key={`route-${index}`}
              >
                {tab.label.toUpperCase()}
              </TabComponent>
            );
          }

          const MenuComponent = tab?.items?.some((t) => t.route === currentUri)
            ? SelectedButton
            : StyledButton;
          return (
            <span key={`span-${index}`}>
              <MenuComponent
                key={`button-${index}`}
                onClick={(evt) => setAnchorEl(evt.currentTarget)}
                variant="text"
              >
                {tab.label.toUpperCase()}
              </MenuComponent>
              <Menu
                key={`button-menu-${index}`}
                anchorEl={anchorEl}
                open={open}
                MenuListProps={{
                  sx: {
                    backgroundColor: ParkkiTheme.colors.lightBlue,
                    fontFamily: '"Source Sans Pro", sans-serif',
                  },
                }}
                sx={{
                  top: "11px",
                }}
                PaperProps={{
                  sx: {
                    borderRadius: "0 0 4px 4px",
                    width: "129px",
                  },
                }}
                onClose={() => setAnchorEl(null)}
                keepMounted
              >
                {tab?.items
                  ?.filter((subTab) => subTab.hasAccess)
                  .map((subTab, subIndex) => {
                    return (
                      <MenuItem
                        onClick={() => {
                          setAnchorEl(null);
                          onTabClick(subTab);
                        }}
                        key={`button-menu-sub-${index}-${subIndex}`}
                        sx={{
                          backgroundColor: ParkkiTheme.colors.lightBlue,
                          color: "white",
                          fontFamily: '"Source Sans Pro", sans-serif',
                          fontSize: "16px",
                          whiteSpace: "nowrap",
                          fontWeight: "normal",
                          lineHeight: "normal",
                          letterSpacing: "normal",
                        }}
                      >
                        {subTab.label.toUpperCase()}
                      </MenuItem>
                    );
                  })}
              </Menu>
            </span>
          );
        })}
    </Container>
  );
}

export default HeaderTabs;
